import { CheckboxBlankCircleOutline } from "mdi-material-ui";
import { alpha } from "@material-ui/core";
import {
  ConsultantQualityCheckDataFragment,
  type ConsultantQualityStatus,
  type PausedStateFragment
} from "@graphql";
import {
  VerifiedInfoIcon,
  Color,
  ForwardIcon,
  RegisterIcon,
  VerifiedIcon,
  WarningIcon,
  EventIcon
} from "@bonliva-crm/ui";
import { isPartiallyExpired } from "./health-declaration-quality-check/utils";
import moment from "moment";

const statusIcons = {
  Approved: VerifiedIcon,
  ApprovedWithRequestSent: VerifiedIcon,
  ApprovedWithComment: VerifiedInfoIcon,
  AwaitingApproval: VerifiedInfoIcon,
  RequestSent: ForwardIcon,
  RequestReceivedNotProcessed: WarningIcon,
  Ordered: EventIcon,
  Booked: EventIcon,
  ConsultantResponsible: RegisterIcon,
  Paused: VerifiedIcon
};

export function statusIcon(data: ConsultantQualityCheckDataFragment) {
  const isHalfExpired = isPartiallyExpired(
    data.Name,
    data.Status,
    data.HasExpired,
    data.StatusSetOn
  );

  if (isHalfExpired) {
    return VerifiedInfoIcon;
  }

  if (data.Status) {
    return statusIcons[data.Status.Name] || CheckboxBlankCircleOutline;
  }

  if (data.AvailableStatuses?.length === 1) {
    return statusIcons[data.AvailableStatuses[0].Name] || CheckboxBlankCircleOutline;
  }

  return CheckboxBlankCircleOutline;
}

export function statusIconColor(data: ConsultantQualityCheckDataFragment) {
  if (!data || !data.Status) {
    return undefined;
  }

  const isHalfExpired = isPartiallyExpired(
    data.Name,
    data.Status,
    data.HasExpired,
    data.StatusSetOn
  );

  if (data.Status.Name === "ApprovedWithComment" || isHalfExpired) {
    return alpha(Color.Orange, 0.5);
  }

  if (data.Status.Name === "AwaitingApproval") {
    return Color.Orange;
  }

  if (data.Status.Name === "Paused") {
    return Color.Orange;
  }

  if (!["Approved", "ApprovedWithRequestSent"].includes(data.Status.Name)) {
    return Color.Gray;
  }

  if (data.ExpiresSoon) {
    return Color.Orange;
  }

  if (data.HasExpired) {
    return Color.ErrorLight;
  }

  return Color.CheckGreen;
}

export function hasStatusParams(
  data: ConsultantQualityCheckDataFragment["Status"]
): data is ConsultantQualityStatus & { params: Record<string, any> } {
  return data && "params" in data && !!data.params;
}

export function hasPausedState(
  data: ConsultantQualityCheckDataFragment["Status"]
): data is ConsultantQualityStatus & { pausedState: PausedStateFragment } {
  return data && "pausedState" in data && !!data.pausedState;
}

export function isPaused(
  data: ConsultantQualityCheckDataFragment["Status"]
): data is ConsultantQualityStatus & { pausedState: PausedStateFragment } {
  if (!hasPausedState(data)) {
    return false;
  }

  return data.pausedState.isPaused;
}

export function formatLabelDate(date: Date | null) {
  return date ? moment.utc(date).format("L") : null;
}
